import { cn } from '@/lib/utils';
import * as ToggleGroup from '@radix-ui/react-toggle-group';

import { createToggleChangeHandler } from './toggle.utils';

export enum ReportTabs {
  LIST = 'list',
  MAP = 'map',
  REPORT = 'report',
}

export type ToggleTabs = ReportTabs.LIST | ReportTabs.MAP | ReportTabs.REPORT;
export type ToggleTabsWithoutReport = Exclude<ToggleTabs, ReportTabs.REPORT>;

export const ProjectDetailsTabs = ReportTabs;
export type ProjectDetailsTabs = ReportTabs;

export const FarmDetailsTabs = ReportTabs;
export type FarmDetailsTabs = ReportTabs;

type ToggleProps<T extends ToggleTabs> = {
  visibleTab: T;
  setVisibleTab: (value: T) => void;
  showReport?: boolean;
  showMap?: boolean;
};

export const Toggle = <T extends ToggleTabs = ToggleTabs>({
  visibleTab,
  setVisibleTab,
  showReport = true,
  showMap = true,
}: ToggleProps<T>) => {
  const handleToggleChange = createToggleChangeHandler(setVisibleTab);

  const toggleGroupItemClasses = 'p-2 rounded text-neutral-500 data-[state=on]:bg-brand data-[state=on]:text-neutral-0';

  return (
    <ToggleGroup.Root type="single" value={visibleTab} onValueChange={handleToggleChange}>
      <div className={cn('flex flex-row justify-between items-center rounded-lg bg-brand-50 w-fit p-2 h-14')}>
        <ToggleGroup.Item value={ReportTabs.LIST} className={toggleGroupItemClasses}>
          List View
        </ToggleGroup.Item>
        {showMap && (
          <ToggleGroup.Item value={ReportTabs.MAP} className={toggleGroupItemClasses}>
            Map View
          </ToggleGroup.Item>
        )}
        {showReport && (
          <ToggleGroup.Item value={ReportTabs.REPORT} className={toggleGroupItemClasses}>
            PDF Report
          </ToggleGroup.Item>
        )}
      </div>
    </ToggleGroup.Root>
  );
};

type FarmToggleProps = {
  visibleTab: ToggleTabs;
  setVisibleTab: (tab: ToggleTabs) => void;
};

export const FarmToggle = ({ visibleTab, setVisibleTab }: FarmToggleProps) => (
  <Toggle visibleTab={visibleTab} setVisibleTab={setVisibleTab} showReport={true} />
);

type ProjectToggleProps = {
  visibleTab: ToggleTabs;
  setVisibleTab: (tab: ToggleTabs) => void;
  showMap?: boolean;
};

export const ProjectToggle = ({ visibleTab, setVisibleTab, showMap = true }: ProjectToggleProps) => (
  <Toggle visibleTab={visibleTab} setVisibleTab={setVisibleTab} showReport={true} showMap={showMap} />
);

type FieldToggleProps = {
  visibleTab: ToggleTabsWithoutReport;
  setVisibleTab: (tab: ToggleTabsWithoutReport) => void;
};

export const FieldToggle = ({ visibleTab, setVisibleTab }: FieldToggleProps) => (
  <Toggle visibleTab={visibleTab} setVisibleTab={setVisibleTab} showReport={false} />
);
